<template>
  <div
    class="relative z-10 flex transform flex-col border whitespace-nowrap transition-all duration-500"
    :class="
      isExpanded ? 'expanded-sidepanel' : 'w-10 -translate-x-5 bg-gradient'
    "
  >
    <div v-show="isExpanded" class="relative flex h-full flex-col">
      <div class="top-0 z-10 h-60 w-full space-y-4 bg-white p-8 pt-7 shadow-md">
        <lf-h3>{{ $t("CONTRACTS.CONTRACT_BUILDER.CONTRACT_FIELDS") }}</lf-h3>
        <lf-dropdown
          :placeholder="$t('COMMON.ROLE')"
          data-cy="role-dropdown"
          name="role"
          :value="selectedRole.role"
          :options="roles"
          :option-formatter="formatRoleOptions"
          full-width
          @change="selectRole"
        >
          <template #type>
            <span
              class="ml-2 rounded-4 bg-gray-200 px-2 text-xxs font-semibold uppercase"
              data-cy="role-index"
            >
              {{ getCountOfRole(selectedRole, roles) }}
            </span>
          </template>
        </lf-dropdown>

        <lf-dropdown-with-api-search
          :placeholder="$t('OFFER_CUSTOMISATION.PRODUCT_TEMPLATE', 1)"
          data-cy="offer-template-dropdown"
          name="offer-template"
          :value="selectedOfferTemplate?.name ?? selectedOfferTemplate?.id"
          :get-paginated-options="getOrganisationOfferTemplates"
          accessor="name"
          identifier="id"
          full-width
          :disabled="!!contractTemplateOfferTemplateId"
          @change="selectOfferTemplate"
        />
        <search-input
          v-model="search"
          data-cy="side-panel-search"
          class="h-10 w-full"
          no-padding
          no-margin
        />
      </div>
      <div class="overflow-y-auto" data-cy="fields">
        <div
          v-for="section in filteredTemplatesOptions"
          :key="section.category"
        >
          <div
            class="flex cursor-pointer items-center justify-between p-7"
            :data-cy="section.category"
            :class="{ 'border-t bg-white': !isOpen[section.category] }"
            @click="toggleSection(section.category)"
          >
            <div class="flex space-x-2 text-xxs">
              <span
                class="rounded-full bg-gray-200 px-1-5 py-0-5 font-semibold text-headline"
              >
                {{ section.fields.length }}
              </span>
              <span
                class="flex items-center font-medium text-gray-500 uppercase"
              >
                {{ section.category }}
              </span>
            </div>
            <button class="cursor-pointer">
              <icon-base
                :class="{ 'rotate-180 transform': isOpen[section.category] }"
                height="6"
                width="10"
                icon="dropdown-arrow"
              />
            </button>
          </div>
          <div
            v-show="isOpen[section.category]"
            class="flex flex-col space-y-4 overflow-y-auto p-4"
            :data-cy="section.category + '-items'"
          >
            <contract-field
              v-for="field in section.fields"
              :key="field.id"
              :field="field"
              @dragstart="startDrag($event, field)"
              @dragend="stopDrag"
            />
          </div>
        </div>
      </div>
    </div>
    <expand-button-white
      class="absolute top-5 -right-2-5 z-10 cursor-pointer shadow-xs"
      color="white"
      :expanded="isExpanded"
      @click="isExpanded = !isExpanded"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";

import { formatRoleOptions, getCountOfRole } from "@/helpers/contractBuilder";
import {
  useContractBuilder,
  useOfferTemplateFields,
} from "@/hooks/contractBuilder";

import ContractField from "@/components/contractBuilder/ContractField.vue";
import ExpandButtonWhite from "@/components/ui/buttons/ExpandButtonWhite.vue";
import LfDropdownWithApiSearch from "@/components/ui/inputs/LfDropdownWithApiSearch.vue";

import type {
  ContractTemplateFieldCategory,
  RoleWithID,
} from "@/models/contractBuilder";
import type { GetOfferTemplatesParams, OfferTemplate } from "@/models/offers";
import type { ContractTemplateField } from "@/models/options";

const OFFER_LEGACY_CATEGORY = "Offer Data (Legacy)";

const emit = defineEmits<{
  "role:select": [number];
  "offer-template:select": [OfferTemplate];
}>();

const props = defineProps<{
  selectedRole: RoleWithID;
  roles: RoleWithID[];
  selectedOfferTemplate?: OfferTemplate | null;
  organizationId?: string;
  contractTemplateFields?: ContractTemplateFieldCategory[];
  contractTemplateOfferTemplateId?: string;
}>();

const { contractTemplatesOptions } = useContractBuilder();
const { fetchOfferTemplates } = useOfferTemplateFields();

const isExpanded = ref(true);
const isDragging = ref(false);
const search = ref("");

const filterFieldsBySearchTerm = (fields: ContractTemplateField[]) => {
  const searchTerm = search.value.toLowerCase();
  return fields.filter((field) =>
    field.name?.toLowerCase().includes(searchTerm)
  );
};

const filteredTemplatesOptions = computed(() => {
  const sections: Array<{ category: string; fields: ContractTemplateField[] }> =
    [];

  const standardFields = contractTemplatesOptions.value?.fields;
  if (standardFields) {
    standardFields.forEach(({ category, fields }) => {
      const filteredFields = filterFieldsBySearchTerm(fields);
      if (filteredFields.length) {
        sections.push({
          category,
          fields: filteredFields,
        });
      }
    });
  }

  if (props.contractTemplateFields?.length) {
    props.contractTemplateFields.forEach((categoryData) => {
      if (!categoryData.fields?.length) {
        return;
      }

      const filteredFields = filterFieldsBySearchTerm(categoryData.fields);
      if (filteredFields.length) {
        sections.push({
          category: categoryData.category || "OFFER DATA",
          fields: filteredFields,
        });
      }
    });
    return sections.filter(
      (section) => section.category !== OFFER_LEGACY_CATEGORY
    );
  }

  return sections;
});

const isOpen = reactive<Record<string, boolean>>({});

watch(
  () => filteredTemplatesOptions.value,
  (newSections) => {
    newSections.forEach(({ category }) => {
      if (isOpen[category] === undefined) {
        isOpen[category] = true;
      }
    });
  },
  { immediate: true }
);

const startDrag = (e: DragEvent, field: ContractTemplateField) => {
  if (!e.dataTransfer) {
    return;
  }

  const dragData = {
    ...field,
    role: { id: props.selectedRole.id, role: props.selectedRole.role },
  };

  e.dataTransfer.setData("text", JSON.stringify(dragData));
  isDragging.value = true;
};

const stopDrag = () => {
  isDragging.value = false;
};

const selectRole = (role: number) => {
  emit("role:select", role);
};

const selectOfferTemplate = (template: OfferTemplate) => {
  emit("offer-template:select", template);
};

const toggleSection = (category: string) => {
  isOpen[category] = !isOpen[category];
};

const getOrganisationOfferTemplates = async (params: {
  search?: string;
  page?: number;
  per_page?: number;
}) => {
  const paramsWithOrganisationId: GetOfferTemplatesParams = { ...params };
  if (props.organizationId) {
    paramsWithOrganisationId.client = props.organizationId;
  } else {
    paramsWithOrganisationId.lendflow = 1;
  }
  return fetchOfferTemplates(paramsWithOrganisationId);
};
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.expanded-sidepanel {
  width: 500px;
  max-width: 500px !important;
  background-color: #f9f9fb;
}
</style>
